import { Button } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * ============================================================================
 * THE PAGER — because `total` was on the wire and nothing read it
 * ============================================================================
 *
 * Three of this app's lists are capped by the SERVER at a fixed page (payments 500,
 * charges 500, documents 200) and the caller supplies only `?offset=`. Until 2026-08-12
 * this SPA sent no offset and rendered no count, so a subscriber with 812 payments was
 * shown the newest 500 — silently, with no line saying so and no control that could reach
 * the rest. On a Model A contract the charge list is one row per linked service PER DAY,
 * so two accounts and seven services cross the cap inside a single month.
 *
 * # It counts rows, never money
 *
 * `from`, `to` and `total` here are ROW INDICES. The arithmetic is index arithmetic, which
 * is the distinction `money-arithmetic.test.ts` is built around and `format.ts` states:
 * getting a row number wrong shows the subscriber the wrong page, which he can see;
 * getting an AMOUNT wrong shows him a figure he cannot check. Not one amount passes
 * through this file.
 *
 * # What it renders, and when
 *
 * The COUNT is shown whenever there is anything to count — «Всего записей: 3» is a fact,
 * and its absence is what let a truncated list look complete. The BUTTONS appear only when
 * the list does not fit one page, because a pair of dead arrows on a three-row table is
 * the D109 control this phase has removed three times.
 *
 * The «Предыдущие» button is disabled at the first page through a BOUND expression, which
 * is the distinction `nav.test.tsx` keeps: a literal `disabled` is a dead lever, a bound
 * one is a control at the end of its range.
 */
export function Pager({
  offset,
  pageSize,
  shown,
  total,
  onOffset,
}: {
  /** The row index this page starts at — what was sent as `?offset=`. */
  offset: number;
  /** The SERVER's page size (see `api/money.ts`, pinned to the Go constants). */
  pageSize: number;
  /** How many rows this page actually carries. */
  shown: number;
  /** The count over the whole filtered set, from the server. */
  total: number;
  onOffset: (next: number) => void;
}) {
  const { t } = useTranslation();
  if (total <= 0 && shown <= 0) return null;

  const first = offset + 1;
  const last = offset + shown;
  const paged = total > pageSize;

  return (
    <div className="pa-pager">
      <span className="pa-pager-count">
        {paged ? t('money.pager.shown', { from: first, to: last, total }) : t('money.pager.all', { total })}
      </span>
      {paged ? (
        <span className="pa-pager-controls">
          <Button
            size="small"
            disabled={offset <= 0}
            onClick={() => onOffset(Math.max(0, offset - pageSize))}
          >
            {t('money.pager.previous')}
          </Button>
          <Button size="small" disabled={last >= total} onClick={() => onOffset(offset + pageSize)}>
            {t('money.pager.next')}
          </Button>
        </span>
      ) : null}
    </div>
  );
}
